/* eslint-disable */
import "babel-polyfill";
import "object-assign";
import _ from "lodash";
import get from "lodash/get";
import last from "lodash/last";
import { generateYScale } from "./MonitorUtils";
import { DateTime } from "luxon";
import { slicePoints } from "./sweeper";

export class CanvasPoints {
  constructor({ chartEndDateTime, dataEndDateTime } = {}) {
    Object.entries({ chartEndDateTime, dataEndDateTime }).forEach(
      ([key, val]) =>
        Object.defineProperty(this, key, {
          value: val && DateTime.fromISO(val, { zone: "utc" }),
          enumerable: false,
          writable: true,
        }),
    );
  }
  slicePoints(params) {
    Object.values(this).forEach(
      (wave) => (wave.points = slicePoints({ ...params, points: wave.points })),
    );
    return this;
  }
  // left-most data coordinate
  get firstX() {
    return _.min(
      Object.values(this)
        .map((wave) => wave.points.find((point) => point?.[0])?.[0])
        .filter((x) => !isNaN(x)),
    );
  }
  // right-most data coordinate.
  get lastX() {
    return Math.max(
      ...Object.values(this).map(
        (wave) =>
          wave.points?.findLast((point) => !isNaN(point?.[1]))?.[0] || 0,
      ),
    );
  }
  get filled() {
    return this.dataEndDateTime > this.chartEndDateTime;
  }
  clone() {
    const { chartEndDateTime, dataEndDateTime } = this;
    const canvasPoints = new CanvasPoints({
      chartEndDateTime,
      dataEndDateTime,
    });
    Object.assign(canvasPoints, _.cloneDeep(this));
    return canvasPoints;
  }
  translate({ xOffset }, mutate) {
    const canvasPoints = mutate ? this : this.clone();
    Object.values(canvasPoints).forEach((wave) => {
      wave.points?.forEach((point) => {
        if (!isNaN(point?.[0])) point[0] += xOffset;
      });
    });
    return canvasPoints;
  }
  prepend(oldCanvasPoints, filter) {
    const sliced = oldCanvasPoints.slicePoints({
      ...filter,
      xMax: this.firstX,
      lastIndexOffset: 1,
    });
    Object.entries(sliced).forEach(([key, wave]) => {
      if (!this[key]?.points) return (this[key] = wave);
      this[key].points = [...wave.points, ...this[key].points];
    });
    return this;
  }
}

if (!Array.prototype.find) {
  Array.prototype.find = function (predicate) {
    if (this == null) {
      throw new TypeError("Array.prototype.find called on null or undefined");
    }
    if (typeof predicate !== "function") {
      throw new TypeError("predicate must be a function");
    }
    var list = Object(this);
    var length = list.length >>> 0;
    var thisArg = arguments[1];
    var value;

    for (var i = 0; i < length; i++) {
      value = list[i];
      if (predicate.call(thisArg, value, i, list)) {
        return value;
      }
    }
    return undefined;
  };
}

export default function plotCharts({
  waveGroups,
  height,
  width,
  slicedWaveforms,
  pixelsPerSecond,
  offsetY,
  chartHeight,
  chartEndTime,
  paddingTop,
  center,
}) {
  const canvasPoints = new CanvasPoints();
  if (!slicedWaveforms.length) {
    return {};
  }

  // Render each waveform.
  waveGroups.forEach((group, i) =>
    plotWave({
      group,
      i,
      height,
      width,
      slicedWaveforms,
      pixelsPerSecond,
      offsetY,
      chartHeight,
      chartEndTime,
      paddingTop,
      center,
      canvasPoints,
    }),
  );
  const lastSegment = _.last(slicedWaveforms);
  const lastTimestamp = lastSegment?.timestamp;
  Object.assign(canvasPoints, {
    dataEndDateTime: DateTime.fromISO(lastTimestamp).plus({ seconds: 1 }),
    chartEndDateTime: DateTime.fromISO(chartEndTime),
  });
  return { canvasPoints, chartEndTime };
}

class WaveCanvasPoints {
  points = [];
}
function plotWave({
  group,
  i,
  height,
  width,
  slicedWaveforms,
  pixelsPerSecond,
  offsetY,
  chartHeight,
  chartEndTime,
  paddingTop = 20,
  center,
  canvasPoints,
}) {
  if (!canvasPoints[group.key])
    canvasPoints[group.key] = new WaveCanvasPoints();

  // Don't bother plotting charts that are off the canvas.
  if (offsetY > (i + 1) * chartHeight || chartHeight * i - offsetY > height) {
    return;
  }

  if (center) {
    const finalSegment = slicedWaveforms?.lastItem?.waveData?.find(
      (wave) => wave.key == group.key,
    );
    const maxRange = finalSegment?.max ?? group.maxRange;
    const pixelRangeY = generateYScale({
      group,
      pixelsPerSecond,
      yOffset: 0,
      chartHeight,
    }).update(finalSegment)(maxRange);
    offsetY = -(height - pixelRangeY) / 2;
  }

  let yScale = generateYScale({
    group,
    pixelsPerSecond,
    yOffset: chartHeight * (i + 1) - offsetY + paddingTop,
    chartHeight,
  });

  const waveCanvasPoints = canvasPoints[group.key];

  slicedWaveforms.forEach((segment, i) => {
    const waveSegment = segment.waveData?.find((wave) => wave.key == group.key);
    // isBlank detects if there are is no data in the wave.
    // This generally shouldn't occur, since it's filtered on the main-thread.
    const isBlank = !waveSegment?.data?.length;
    // hasGap is true if the second being parse is non-consecutive.
    const hasGap = !!(
      i &&
      new Date(segment.timestamp) - new Date(slicedWaveforms[i - 1].timestamp) >
        1000
    );
    // Adding a null to the the array of points will force a line-break.
    if (isBlank || hasGap) {
      waveCanvasPoints.points.push(null);
      if (isBlank) return;
    }

    yScale = yScale.update(waveSegment);
    const pointsPerSecond = waveSegment.data.length;
    const frameTimeToEnd_ms =
      new Date(segment.timestamp) - new Date(chartEndTime);
    waveSegment.data.forEach((point, pointIndex) =>
      plotPoint({
        point,
        pointIndex,
        pointsPerSecond,
        width,
        frameTimeToEnd_ms,
        pixelsPerSecond,
        waveCanvasPoints,
        segment,
        yScale,
      }),
    );
  });
}

function plotPoint({
  point,
  pointIndex,
  pointsPerSecond,
  width,
  frameTimeToEnd_ms,
  pixelsPerSecond,
  waveCanvasPoints,
  yScale,
}) {
  if (point === null) {
    return waveCanvasPoints.points.push(null);
  }
  const fractionOfSecond = pointIndex / pointsPerSecond;
  const x =
    (frameTimeToEnd_ms / 1000 + fractionOfSecond) * pixelsPerSecond + width;
  if (x < 0) {
    return;
  }
  const canvasPoint = [x, yScale(point)];

  waveCanvasPoints.points.push(canvasPoint);
}
